import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed, type Signal, signal } from '@preact/signals-core';
import { css, type CSSResult, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import { renderUniLit } from '../index.js';
import type {
  CellRenderProps,
  DataViewCellLifeCycle,
} from '../property/index.js';
import type { Property } from '../view-manager/property.js';

export type DataViewCellDisplayMode = 'expanded' | 'collapsed' | 'smart';

export function shouldExpandDataViewCell({
  isEditing,
  mode,
  rowSelected,
}: {
  isEditing: boolean;
  mode: DataViewCellDisplayMode;
  rowSelected: boolean;
}): boolean {
  if (isEditing) return true;
  if (mode === 'expanded') return true;
  if (mode === 'collapsed') return false;
  return rowSelected;
}

export function dataViewCellContainerStyles(
  selector: string,
  options: {
    rootBoxSizing?: 'border-box' | 'content-box';
    includeRootHeight?: boolean;
  } = {}
): CSSResult {
  const rootBoxSizing = options.rootBoxSizing ?? 'border-box';
  const rootHeight = options.includeRootHeight === false ? '' : 'height: 100%;';

  return css`
    ${unsafeCSS(selector)} {
      display: flex;
      align-items: start;
      width: 100%;
      ${unsafeCSS(rootHeight)}
      border: none;
      outline: none;
      box-sizing: ${unsafeCSS(rootBoxSizing)};
    }

    ${unsafeCSS(selector)} * {
      box-sizing: border-box;
    }

    ${unsafeCSS(selector)} uni-lit > *:first-child {
      padding: 6px;
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }

    ${unsafeCSS(selector)}:not([data-expanded='true']) {
      height: 36px;
      overflow: hidden;
      --dv-cell-white-space: nowrap;
      --dv-cell-overflow: hidden;
      --dv-cell-text-overflow: ellipsis;
    }

    ${unsafeCSS(selector)}[data-expanded='true'] {
      height: auto;
      min-height: 36px;
      overflow: visible;
      --dv-cell-white-space: normal;
      --dv-cell-overflow: visible;
      --dv-cell-text-overflow: unset;
      --dv-cell-word-break: break-all;
      align-items: flex-start;
    }
  `;
}

/**
 * View-agnostic cell wrapper used by every data-view preset (table,
 * gantt, ...) to render a property's cell renderer with consistent
 * chrome: outer flex layout, 6 px padding on the renderer root,
 * overflow + ellipsis variables, and a collapsed/expanded switch.
 *
 * Presets pass their own `property`, `rowId`, and an editing signal.
 * The element has NO knowledge of selection or group membership —
 * that lives on the preset's own container (the table one still
 * extends this for selection-aware click handling). Keeping the
 * cell chrome in one place means the table and gantt views produce
 * pixel-identical cells for the shared property renderers (member,
 * created-by, rich-text, ...).
 */
export class DataViewCellContainer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = dataViewCellContainerStyles(
    'dv-data-view-cell-container'
  );

  private readonly _cell$ = signal<DataViewCellLifeCycle>();

  @property({ attribute: false })
  accessor property!: Property;

  @property({ attribute: false })
  accessor rowId!: string;

  /**
   * External editing signal owned by the caller. The cell renderer
   * watches it to switch between read and edit modes. Optional —
   * when absent, the cell is never in edit mode (useful for read-only
   * embeddings like gantt swimlane labels).
   */
  @property({ attribute: false })
  accessor isEditing$: Signal<boolean> | undefined = undefined;

  /**
   * Optional expanded override. When set, forces the collapsed vs
   * expanded state explicitly. When absent the cell stays collapsed.
   * Table view wires this up to its cell-display-mode signal; gantt
   * leaves it undefined.
   */
  @property({ attribute: false })
  accessor expanded$: Signal<boolean> | undefined = undefined;

  /**
   * Called when the renderer (or this container's click handler, if
   * `enterEditOnClick` is on) wants to enter / leave edit mode.
   * Passing `undefined` leaves edit mode entirely in the caller's
   * hands — useful for gantt, where edit is gated by a double-click
   * above the cell instead of a single click on it.
   */
  @property({ attribute: false })
  accessor onSelectCurrentCell: ((editing: boolean) => void) | undefined =
    undefined;

  /**
   * Whether a single click on the cell should enter edit mode. Table
   * view enables this to match its click-to-edit affordance; gantt
   * leaves it off so single-clicks reach the row-selection handler
   * underneath, and dblclick (bound on the caller's wrapper) is the
   * only path into edit mode.
   */
  @property({ attribute: false, type: Boolean })
  accessor enterEditOnClick: boolean = false;

  cell$ = computed(() => {
    return this.property.cellGetOrCreate(this.rowId);
  });

  get cell(): DataViewCellLifeCycle | undefined {
    return this._cell$.value;
  }

  private readonly internalEditing$ = signal(false);

  private get editingSignal(): Signal<boolean> {
    return this.isEditing$ ?? this.internalEditing$;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._disposables.addFromEvent(
      this,
      'click',
      event => {
        if (!this.enterEditOnClick) return;
        if (event.shiftKey || event.ctrlKey || event.metaKey) return;
        if (this.editingSignal.value) return;
        if (this.property.valueReadonly$.value) return;
        // Checkbox renderers own both their click action and cell selection.
        // Calling their edit hook here would perform the same action twice.
        if (this.property.type$.value === 'checkbox') return;
        if (this.cell?.beforeEnterEditMode() === false) return;
        this.cell?.setEditingPoint?.({
          clientX: event.clientX,
          clientY: event.clientY,
        });
        this.onSelectCurrentCell?.(true);
      },
      // Rich-text and popover cells may consume the bubbling click.
      // Activate their editor before the event reaches those descendants.
      { capture: true }
    );
  }

  override render(): unknown {
    const renderer = this.property.renderer$.value;
    if (!renderer) return nothing;
    const isEditing = this.editingSignal.value;
    this.property.view.lockRows(isEditing);
    this.dataset['editing'] = `${isEditing}`;
    const expanded = shouldExpandDataViewCell({
      isEditing,
      mode: this.expanded$?.value ? 'expanded' : 'collapsed',
      rowSelected: false,
    });
    this.dataset['expanded'] = `${expanded}`;
    const props: CellRenderProps = {
      cell: this.cell$.value,
      isEditing$: this.editingSignal,
      selectCurrentCell: (editing: boolean) => {
        this.onSelectCurrentCell?.(editing);
      },
      singleLine: !expanded,
    };
    return renderUniLit(renderer.view, props, {
      ref: this._cell$,
      style: { display: 'contents' },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-data-view-cell-container': DataViewCellContainer;
  }
}
